import React, { useState } from 'react';
import { Mail, ArrowLeft, Check, X } from 'lucide-react';
import { auth } from '../components/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('input');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage('Please enter your email address');
      setStep('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setStep('error');
      return;
    }

    setStep('loading');
    
    try {
      await sendPasswordResetEmail(auth, email);
      setStep('success');
    } catch (error) {
      console.error('Firebase Error:', error);
      
      if (error.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address');
      } else if (error.code === 'auth/user-not-found') {
        setErrorMessage('This email is not registered. Please sign up first.');
      } else if (error.code === 'auth/too-many-requests') {
        setErrorMessage('Too many attempts. Please try again later.');
      } else {
        setErrorMessage('Failed to send reset email. Please try again.');
      }
      setStep('error');
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/signin';
  };

  return (
    <div style={styles.container}>
      <style>{keyframes}</style>

      <div style={styles.card}>
        
        <button onClick={handleBackToLogin} style={styles.backButton}>
          <ArrowLeft size={20} />
          <span>Back to Login</span>
        </button>

        {step === 'input' && (
          <div style={styles.content}>
            <div style={styles.iconWrapper}>
              <Mail size={32} style={styles.iconMail} />
            </div>
            
            <h1 style={styles.title}>Forgot Password?</h1>
            <p style={styles.description}>
              No worries! Enter your email address and we'll send you instructions to reset your password.
            </p>

            <div style={styles.formContainer}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <div style={styles.inputWrapper}>
                  <Mail size={18} style={styles.inputIcon} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit(e)}
                    placeholder="you@example.com"
                    style={styles.input}
                  />
                </div>
              </div>

              <button onClick={handleSubmit} style={styles.submitButton}>
                Send Reset Link
              </button>
            </div>
          </div>
        )}

        {step === 'loading' && (
          <div style={styles.content}>
            <div style={styles.loadingContainer}>
              <div style={styles.envelopeWrapper}>
                <div style={styles.envelopeBack}></div>
                <div style={styles.envelopeFront}></div>
                <div style={styles.envelopeTop}></div>
                <div style={styles.letter}></div>
              </div>
            </div>
            
            <h1 style={styles.title}>Sending Reset Link...</h1>
            <p style={styles.description}>
              Please wait while we process your request.
            </p>
          </div>
        )}

        {step === 'success' && (
          <div style={styles.content}>
            <div style={styles.successIconWrapper}>
              <div style={styles.checkCircle}>
                <Check size={40} style={styles.checkIcon} />
              </div>
            </div>
            
            <h1 style={styles.title}>Check Your Email</h1>
            <p style={styles.emailSent}>
              If an account exists for
            </p>
            <p style={styles.emailDisplay}>{email}</p>
            <p style={styles.emailNote}>
              you will receive password reset instructions.
            </p>

            <button onClick={handleBackToLogin} style={styles.submitButton}>
              Return to Login
            </button>

            <button 
              onClick={() => {
                setStep('input');
                setEmail('');
              }} 
              style={styles.resendButton}
            >
              Try another email
            </button>
          </div>
        )}

        {step === 'error' && (
          <div style={styles.content}>
            <div style={styles.errorIconWrapper}>
              <div style={styles.errorCircle}>
                <X size={40} style={styles.errorIcon} />
              </div>
            </div>
            
            <h1 style={styles.title}>Unable to Send</h1>
            <p style={styles.errorText}>{errorMessage}</p>

            <button 
              onClick={() => {
                setStep('input');
                setErrorMessage('');
              }} 
              style={styles.submitButton}
            >
              Try Again
            </button>

            <button 
              onClick={() => window.location.href = '/signup'} 
              style={styles.signupButton}
            >
              Create New Account
            </button>
          </div>
        )}

      </div>

      <div style={styles.footer}>
        <p style={styles.footerText}>
          Remember your password? 
          <button onClick={handleBackToLogin} style={styles.footerLink}>
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}

const keyframes = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes envelopeFloat {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes flapOpen {
    0% {
      transform: rotateX(0deg);
    }
    50% {
      transform: rotateX(-180deg);
    }
    100% {
      transform: rotateX(0deg);
    }
  }

  @keyframes letterSlide {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes checkScale {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes errorShake {
    0%, 100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-10px);
    }
    75% {
      transform: translateX(10px);
    }
  }
`;

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    backgroundColor: '#FFFFFF',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '48px 40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(229, 231, 235, 0.8)',
    animation: 'slideUp 0.6s ease-out'
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    marginBottom: '32px',
    backgroundColor: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6B7280',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  content: {
    animation: 'fadeIn 0.4s ease-out'
  },
  iconWrapper: {
    width: '72px',
    height: '72px',
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  iconMail: {
    color: '#374151'
  },
  loadingContainer: {
    width: '100%',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  envelopeWrapper: {
    position: 'relative',
    width: '80px',
    height: '60px',
    animation: 'envelopeFloat 2s ease-in-out infinite'
  },
  envelopeBack: {
    position: 'absolute',
    width: '80px',
    height: '60px',
    backgroundColor: '#F3F4F6',
    borderRadius: '4px',
    border: '2px solid #9CA3AF'
  },
  envelopeFront: {
    position: 'absolute',
    bottom: 0,
    width: 0,
    height: 0,
    borderLeft: '40px solid transparent',
    borderRight: '40px solid transparent',
    borderBottom: '30px solid #E5E7EB'
  },
  envelopeTop: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: 0,
    borderLeft: '40px solid transparent',
    borderRight: '40px solid transparent',
    borderTop: '30px solid #9CA3AF',
    transformOrigin: 'top center',
    animation: 'flapOpen 2s ease-in-out infinite'
  },
  letter: {
    position: 'absolute',
    top: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '40px',
    backgroundColor: 'white',
    borderRadius: '2px',
    border: '1px solid #D1D5DB',
    animation: 'letterSlide 2s ease-in-out infinite'
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#111827',
    textAlign: 'center',
    marginBottom: '12px',
    letterSpacing: '-0.02em'
  },
  description: {
    fontSize: '15px',
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: '1.6',
    marginBottom: '32px'
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    color: '#9CA3AF',
    pointerEvents: 'none'
  },
  input: {
    width: '100%',
    padding: '14px 16px 14px 48px',
    fontSize: '15px',
    border: '1.5px solid #E5E7EB',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: '#F9FAFB',
    color: '#111827',
    boxSizing: 'border-box'
  },
  submitButton: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#8B5CF6',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.25)'
  },
  successIconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  checkCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '3px solid #10B981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'checkScale 0.6s ease-out'
  },
  checkIcon: {
    color: '#10B981',
    strokeWidth: 3
  },
  emailSent: {
    fontSize: '15px',
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: '8px'
  },
  emailDisplay: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#8B5CF6',
    textAlign: 'center',
    marginBottom: '8px',
    wordBreak: 'break-all'
  },
  emailNote: {
    fontSize: '14px',
    color: '#9CA3AF',
    textAlign: 'center',
    marginBottom: '32px',
    lineHeight: '1.5'
  },
  resendButton: {
    width: '100%',
    padding: '12px 24px',
    marginTop: '12px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6B7280',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  errorIconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  errorCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '3px solid #EF4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'errorShake 0.6s ease-out'
  },
  errorIcon: {
    color: '#EF4444',
    strokeWidth: 3
  },
  errorText: {
    fontSize: '15px',
    color: '#DC2626',
    textAlign: 'center',
    lineHeight: '1.6',
    marginBottom: '32px',
    fontWeight: '500'
  },
  signupButton: {
    width: '100%',
    padding: '12px 24px',
    marginTop: '12px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#8B5CF6',
    backgroundColor: '#F5F3FF',
    border: '1px solid #DDD6FE',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  footer: {
    marginTop: '32px',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0
  },
  footerLink: {
    marginLeft: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#8B5CF6',
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0
  }
};